<template lang="pug">
  #unassigned-members
    WtTitle(card-title) Unassigned Members
    WtButton.is-size-5.AutomaticallyAddAllUnassignedMembers(
      size="small"
      color="grey"
      isText
      bold
      click
      @click.native="automaticallyAssignMembers"
      :disabled="_.isEmpty(_.get(unassignedMembers, 'data', null)) || loading"
    ) Automatically Add All Unassigned Members to a Team
    WtEntityTable(
      :key="unassignedMembers ? unassignedMembers.data.length : null"
      title="Unassigned Members"
      :showTitle="false"
      :data="unassignedMembers ? unassignedMembers.data : []"
      :pagination-object="unassignedMembers ? unassignedMembers.pagination : null"
      :fetch-action="fetchUnassignedMembers"
      defaultSortField="username"
      defaultSortDirection="asc"
      :empty-message="emptyMessageText"
      :enableSearch="true"
      @search="onSearch"
      searchPlaceholder="Search by username or email..."
      :searchString="searchString"
    )
      BTableColumn(field="select" label="Select" v-slot="column" width="auto")
        BCheckbox(:value="isMemberSelected(column.row)" @input="(value) => selectMember(value, column.row)")
      BTableColumn(field="username" sortable v-slot="column" width="30%" cell-class="has-text-hover" label="Usernamne")
        WtTableCellLongText(:text="column.row.username")
      BTableColumn(field="email" sortable v-slot="column" width="50%" cell-class="has-text-hover" label="Email")
        WtTableCellLongText(:text="column.row.email")
    .buttons.is-pulled-right.mt-5
      WtButton(
        bold
        color="grey"
        size="medium"
        outlined
        :isRouterLink="false"
        :disabled="loading"
        @click.native="openSelectedMembersModal()"
      ) Assign Selected User(s) to a Team
    BModal(
      :active.sync="modalOpen"
      @onCancel="modalOpen = false; selectedTeam = null"
      width="500px"
    )
      .modal-card(style="width: auto")
        header.modal-card-head
          p.modal-card-title.has-text-centered Add {{ selectedMembers.length }}  Selected Members to a Team
        section.modal-card-body
          .columns
            .column
              WtEntityTable(
                title="Select a team"
                :show-title="false"
                :data="_.get(teamsResponseObject, 'data', [])"
                :pagination-object="teamsResponseObject.pagination"
                :fetch-action="fetchTeamsAction"
                default-sort-field="name"
                empty-message="No teams exist in this program yet."
                enable-search
                @search="$emit('searchTeams', $event)"
                searchPlaceholder="Search by team name..."
              )
                BTableColumn(field="select" label="Select" width="40" v-slot="column")
                  BRadio(
                    v-model="selectedTeam"
                    :name="column.row.name"
                    :native-value="column.row"
                  )
                BTableColumn(field="name" label="Team Name" sortable v-slot="column")
                  | {{ column.row.name }}
                BTableColumn(field="size" label="Team Size" sortable v-slot="column")
                  | {{ column.row.numMembers }}/{{ column.row.maxMembers }}
        footer.modal-card-foot.is-flex.is-justify-content-flex-end
          WtButton(color="red" @click.native="modalOpen = false; selectedTeam = null") CANCEL
          WtButton(
            :disabled="!selectedTeam"
            @click.native="individuallyAssignMembers"
          )
            .is-uppercase Assign to a Team
</template>

<script>
import { mapActions } from "vuex";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtTile from "@@/WtTile/WtTile";
import WtEntityTable from "@@/WtEntityTable/WtEntityTable";
import WtTableCellLongText from "@@/WtTableCellLongText/WtTableCellLongText";

export default {
  name: "TeamMembers",

  components: {
    WtButton,
    WtTitle,
    WtTile,
    WtEntityTable,
    WtTableCellLongText
  },

  props: {
    teamsResponseObject: {
      type: Object,
      required: true
    },

    fetchTeamsAction: {
      type: Function,
      required: true
    }
  },

  data() {
    return {
      searching: false,
      loading: false,
      modalOpen: false,
      selectedTeam: null,
      unassignedMembers: null,
      selectedMembers: [],
      fetchUnassignedMembersError: null,
      perPage: 7,
      searchTimeoutId: null,
      searchString: null
    };
  },

  computed: {
    emptyMessageText() {
      return this.searching
        ? "No unassigned members found with that username."
        : "No unassigned members.";
    },

    isTeamAdmin() {
      // auth-user created the team or is a team leader, or is program-admin or wt-admin
      return this.isAuthUserTeamAdmin || this.$auth.user().isAdmin;
    }
  },

  async mounted() {
    this.onSearch = this._.debounce(async query => {
      query === "" ? (this.searching = false) : (this.searching = true);
      this.searchString = query;
      this.fetchUnassignedMembers();
    }, 500);

    await this.fetchUnassignedMembers();
  },

  methods: {
    ...mapActions("teams", ["removeMembersFromTeam", "addMembersToTeam"]),
    ...mapActions("program", ["fetchUnassignedMembers", "fetchProgramTeams"]),
    ...mapActions(["snackBar"]),

    async fetchUnassignedMembers(payload) {
      let page,
        sortColumn,
        sortOrder = null;
      if (payload) {
        page = payload.page;
        sortColumn = payload.sortColumn;
        sortOrder = payload.sortOrder;
      }

      const programId = this.$auth.user().programId;
      try {
        let params = {
          page,
          perPage: this.perPage,
          sortColumn,
          sortOrder,
          searchString: this.searchString
        };
        const response = await this.axios.get(
          `programs/${programId}/members-not-in-team`,
          { params }
        );
        this.unassignedMembers = response.data;
      } catch (error) {
        this.fetchUnassignedMembersError = error;
      }
    },

    async automaticallyAssignMembers() {
      this.$buefy.dialog.confirm({
        title: "Assign All Members to an Open Team",
        confirmText: "Confirm",
        type: "is-primary",
        hasIcon: false,
        message:
          "Unassigned members will automatically be added to teams with open seats. If there are not enough open teams for all members, increase team size or create additional teams.",
        onConfirm: async () => {
          this.loading = true;

          try {
            const response = await this.axios.post(
              "teams/automatically-assign-members"
            );

            let message;
            if (response.data.data["members_assigned"] === 0) {
              message =
                "No members assigned. Create more teams or increase team size.";
            } else if (response.data.data["members_assigned"] > 0) {
              message =
                response.data.data["members_assigned"] ===
                this.unassignedMembers.pagination.total
                  ? "Members assigned to teams successfully."
                  : "Not all members assigned. Create more teams or increase team size. ";
            }

            this.snackBar({
              type: "is-success",
              message
            });
            await this.refreshData();
          } catch (error) {
            this.message =
              "There was an error. Please contact wellness@be-engaged.co";
          }
          this.loading = false;
        }
      });
    },

    selectMember(newValue, member) {
      if (newValue) {
        this.selectedMembers.push(member);
      } else {
        this.selectedMembers = this.selectedMembers.filter(
          m => m.id !== member.id
        );
      }
    },

    isMemberSelected(member) {
      return Boolean(this.selectedMembers.find(m => m.id === member.id));
    },

    openSelectedMembersModal() {
      if (this.selectedMembers.length === 0) {
        this.snackBar({
          type: "is-error",
          message: "Please select users to assign."
        });
      } else {
        this.modalOpen = true;
      }
    },

    async individuallyAssignMembers() {
      if (
        this.selectedMembers.length >
        this.selectedTeam.maxMembers - this.selectedTeam.numMembers
      ) {
        this.snackBar({
          type: "is-error",
          message:
            "There is not enough space on the selected team. Select another team or add fewer members."
        });
      } else {
        this.loading = true;
        this.modalOpen = false;
        await this.addMembersToTeam({
          team: this.selectedTeam,
          memberIds: this.selectedMembers.map(m => m.id)
        });
        await this.refreshData();
        this.selectedTeam = null;
        this.selectedMembers = [];
        this.loading = false;
      }
    },

    onSearch() {
      // defining this here, but is overwritten in the
      // mounted call.  Using debounce requires this
      // approach in order for it to work when
      // the component instance is available.
    },

    async refreshData() {
      await Promise.all([
        this.fetchProgramTeams(),
        this.fetchUnassignedMembers()
      ]);
    }
  }
};
</script>

<style lang="sass" scoped>
.AutomaticallyAddAllUnassignedMembers
  max-width: 100%
  white-space: break-spaces
</style>
